export function questionnaireMachine({
    questions_json,
    settings_json,
    state,
    answer = null
}) {
    let questions;
    let settings;

    try {
        questions = JSON.parse(questions_json);
        settings = JSON.parse(settings_json || "{}");
    } catch {
        return {
            state,
            response: {
                type: "error",
                error: {
                    code: "INVALID_SCHEMA",
                    message: "Invalid questionnaire configuration"
                }
            }
        };
    }

    // -------------------------------
    // INITIAL STATE (only once)
    // -------------------------------
    if (!state) {
        const firstQuestion = questions[0];

        return {
            state: {
                current_question_id: firstQuestion.id,
                answers: {},
                completed: false
            },
            response: {
                type: "question",
                question: firstQuestion
            }
        };
    }


    // -------------------------------
    // ALREADY COMPLETED
    // -------------------------------
    if (state?.completed) {
        return {
            state,
            response: {
                type: "outcome",
                outcome: state.outcome,
                outcome_description: state.outcome_description ?? null
            }
        };
    }

    const currentQuestion = questions.find(
        q => q.id === state.current_question_id
    );


    if (!currentQuestion) {
        return {
            state,
            response: {
                type: "error",
                error: {
                    code: "INVALID_STATE",
                    message: "Current question not found"
                }
            }
        };
    }

    // -------------------------------
    // ASK CURRENT QUESTION
    // -------------------------------
    if (!answer) {
        return {
            state,
            response: {
                type: "question",
                question: currentQuestion
            }
        };
    }

    // -------------------------------
    // RECORD ANSWER
    // -------------------------------
    const newAnswers = {
        ...(state?.answers || {}),
        [currentQuestion.id]: { answer }
    };

    const selectedOption = currentQuestion.options?.find(
        o => String(o.text).trim() === String(answer).trim()
    );

    const logic = selectedOption?.logic;

    // -------------------------------
    // SUBMIT ACTION
    // -------------------------------
    if (logic?.action === "submit") {
        const isDefault = !logic.endPageId || logic.endPageId === "default";
        const endPage =
            settings.endPages?.find(p => p.id === logic.endPageId) || null;

        const outcome = isDefault ? "Complete" : endPage?.name ?? "Complete";
        const outcome_description = endPage?.description ?? null;

        return {
            state: {
                current_question_id: null,
                answers: newAnswers,
                completed: true,
                outcome,
                outcome_description
            },
            response: {
                type: "outcome",
                outcome,
                outcome_description,
                save: {
                    question_id: currentQuestion.id,
                    question_label: currentQuestion.label,
                    answer,
                    outcome,
                    outcome_description
                }
            }
        };
    }

    // -------------------------------
    // GOTO ACTION
    // -------------------------------
    if (logic?.action === "goto") {
        const target = questions.find(q => q.id === logic.targetId);

        if (!target) {
            return {
                state,
                response: {
                    type: "error",
                    error: {
                        code: "INVALID_LOGIC",
                        message: "Target question not found"
                    }
                }
            };
        }

        return {
            state: {
                current_question_id: target.id,
                answers: newAnswers,
                completed: false
            },
            response: {
                type: "question",
                question: target,
                save: {
                    question_id: currentQuestion.id,
                    question_label: currentQuestion.label,
                    answer,
                    outcome: null,
                    outcome_description: null
                }
            }
        };
    }

    // -------------------------------
    // DEFAULT NEXT QUESTION
    // -------------------------------
    const currentIndex = questions.findIndex(q => q.id === currentQuestion.id);
    const nextQuestion = questions[currentIndex + 1];

    if (!nextQuestion) {
        return {
            state: {
                current_question_id: null,
                answers: newAnswers,
                completed: true,
                outcome: "Complete",
                outcome_description: null
            },
            response: {
                type: "outcome",
                outcome: "Complete",
                outcome_description: null,
                save: {
                    question_id: currentQuestion.id,
                    question_label: currentQuestion.label,
                    answer,
                    outcome: "Complete",
                    outcome_description: null
                }
            }
        };
    }

    return {
        state: {
            current_question_id: nextQuestion.id,
            answers: newAnswers,
            completed: false
        },
        response: {
            type: "question",
            question: nextQuestion,
            save: {
                question_id: currentQuestion.id,
                question_label: currentQuestion.label,
                answer,
                outcome: null,
                outcome_description: null
            }
        }
    };
}
